import React, { useCallback, useState } from 'react';
import ComponentCard from '../../components/common/ComponentCard';
import SearchableSelect from '../../components/common/SearchableSelect';
import Label from '../../components/form/Label';
import Input from '../../components/form/input/InputField';
import { api, apiEndpoints } from '../../utils/apiService';

export interface IntegrationsTabProps {
  storeOptions: { value: number; label: string }[];
}

type ManualIntegrationType = '' | 'poshub-askarthur';

const MANUAL_INTEGRATION_OPTIONS: Array<{
  value: Exclude<ManualIntegrationType, ''>;
  label: string;
}> = [{ value: 'poshub-askarthur', label: 'POSHUB <> AskArthur' }];

export default function IntegrationsTab({
  storeOptions,
}: IntegrationsTabProps) {
  const [selectedManualIntegration, setSelectedManualIntegration] =
    useState<ManualIntegrationType>('');
  const [urlDump, setUrlDump] = useState('');
  const [storeId, setStoreId] = useState<string>('');
  const [accountId, setAccountId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [parseMessage, setParseMessage] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCommandOutput, setErrorCommandOutput] = useState<string | null>(
    null,
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [successPayload, setSuccessPayload] = useState<unknown>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);
      setErrorCommandOutput(null);
      setFieldErrors({});
      setSuccessPayload(null);
      setSuccessMessage(null);

      const sid = Number(storeId);
      if (!selectedManualIntegration) {
        setError('Select an integration.');
        return;
      }
      if (!storeId || Number.isNaN(sid) || sid < 1) {
        setError('Select a store.');
        return;
      }
      if (!accountId.trim() || !locationId.trim() || !connectionId.trim()) {
        setError('Account ID, Location ID, and Connection ID are required.');
        return;
      }

      const body: Record<string, unknown> = {
        store_id: sid,
        account_id: accountId.trim(),
        location_id: locationId.trim(),
        connection_id: connectionId.trim(),
      };

      setSubmitting(true);
      try {
        const response = await api.post(apiEndpoints.poshubManualSetup(), body);
        const data = response ? await response.json() : null;

        if (!response?.ok) {
          if (typeof data?.command_output === 'string') {
            setErrorCommandOutput(data.command_output);
          }
          if (data?.errors && typeof data.errors === 'object') {
            const next: Record<string, string[]> = {};
            Object.entries(data.errors).forEach(([k, v]) => {
              next[k] = Array.isArray(v) ? v.map(String) : [String(v)];
            });
            setFieldErrors(next);
            setError('Please correct the fields below.');
          } else if (data?.error === 'setup_failed' && data?.detail) {
            setError(String(data.detail));
          } else if (!data?.errors) {
            setError(
              typeof data?.detail === 'string'
                ? data.detail
                : `Request failed (${response?.status ?? '?'})`,
            );
          }
          return;
        }

        if (data?.ok) {
          setSuccessPayload(data);
          setSuccessMessage(
            'Manual POSHub integration setup completed successfully.',
          );
          setUrlDump('');
          setStoreId('');
          setAccountId('');
          setLocationId('');
          setConnectionId('');
          setParseMessage(null);
        } else {
          setError('Unexpected response from server.');
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Network or unexpected error.',
        );
      } finally {
        setSubmitting(false);
      }
    },
    [selectedManualIntegration, storeId, accountId, locationId, connectionId],
  );

  const handleManualIntegrationChange = useCallback((value: string) => {
    setSelectedManualIntegration(value as ManualIntegrationType);
    setError(null);
    setErrorCommandOutput(null);
    setFieldErrors({});
    setSuccessPayload(null);
    setSuccessMessage(null);
    setParseMessage(null);
  }, []);

  const handleParseUrlDump = useCallback(() => {
    const trimmed = urlDump.trim();
    if (!trimmed) {
      setParseMessage('Paste a callback URL first.');
      return;
    }

    try {
      const parsedUrl = new URL(trimmed);
      const params = parsedUrl.searchParams;

      const parsedAccountId =
        params.get('account_id') || params.get('accountId') || '';
      const parsedLocationId =
        params.get('location_id') || params.get('locationId') || '';
      const parsedConnectionId = params.get('connectionId') || '';
      const parsedStoreId = params.get('storeId') || '';

      if (parsedAccountId) setAccountId(parsedAccountId);
      if (parsedLocationId) setLocationId(parsedLocationId);
      if (parsedConnectionId) setConnectionId(parsedConnectionId);
      if (
        parsedStoreId &&
        parsedStoreId !== 'undefined' &&
        !Number.isNaN(Number(parsedStoreId)) &&
        Number(parsedStoreId) > 0
      ) {
        setStoreId(parsedStoreId);
      }

      setError(null);
      setErrorCommandOutput(null);
      setFieldErrors({});
      setSuccessMessage(null);

      const populatedFields = [
        parsedAccountId && 'Account ID',
        parsedLocationId && 'Location ID',
        parsedConnectionId && 'Connection ID',
        parsedStoreId &&
          parsedStoreId !== 'undefined' &&
          !Number.isNaN(Number(parsedStoreId)) &&
          Number(parsedStoreId) > 0 &&
          'Store',
      ].filter(Boolean);

      if (populatedFields.length === 0) {
        setParseMessage('No supported fields were found in that URL.');
        return;
      }

      setParseMessage(`Filled ${populatedFields.join(', ')} from pasted URL.`);
    } catch {
      setParseMessage('That does not look like a valid URL.');
    }
  }, [urlDump]);

  const selectOptions = storeOptions.map((o) => ({
    value: o.value,
    label: o.label,
  }));

  return (
    <div className="space-y-6">
      <ComponentCard title="Manual Integrations">
        <div className="max-w-2xl space-y-5">
          <div>
            <Label>
              Integration <span className="text-error-500">*</span>
            </Label>
            <select
              value={selectedManualIntegration}
              onChange={(e) => handleManualIntegrationChange(e.target.value)}
              className="h-11 w-full rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-800 shadow-theme-xs dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            >
              <option value="">Select an integration...</option>
              {MANUAL_INTEGRATION_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {selectedManualIntegration === 'poshub-askarthur' ? (
            <>
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-4 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-900/50 dark:text-gray-300">
                <p className="font-medium text-gray-900 dark:text-white">
                  POSHUB &lt;&gt; AskArthur
                </p>
                <p className="mt-1">
                  Uses `POST /api/integrations/poshub/manual-setup/` and submits
                  the selected store as `store_id` plus the required POSHub IDs.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <Label>Callback URL dump</Label>
                  <p className="mb-2 mt-1 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                    Paste the full POSHub callback URL to auto-fill Account ID,
                    Location ID, Connection ID, and Store when available.
                  </p>
                  <textarea
                    value={urlDump}
                    onChange={(e) => {
                      setUrlDump(e.target.value);
                      setParseMessage(null);
                      setSuccessMessage(null);
                    }}
                    rows={4}
                    placeholder="Paste full callback URL here..."
                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm text-gray-800 shadow-theme-xs focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
                  />
                  <div className="mt-3 flex items-center gap-3">
                    <button
                      type="button"
                      onClick={handleParseUrlDump}
                      className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                    >
                      Parse URL dump
                    </button>
                    {parseMessage ? (
                      <p className="text-sm text-gray-600 dark:text-gray-300">
                        {parseMessage}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div>
                  <Label>
                    Store <span className="text-error-500">*</span>
                  </Label>
                  <SearchableSelect
                    options={selectOptions}
                    value={storeId}
                    onChange={(v) => {
                      setStoreId(v);
                      setSuccessMessage(null);
                    }}
                    placeholder="Select a store..."
                    required
                  />
                  {fieldErrors.store_id?.length ? (
                    <p className="mt-1 text-sm text-error-500">
                      {fieldErrors.store_id.join(' ')}
                    </p>
                  ) : null}
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label>
                      Account ID <span className="text-error-500">*</span>
                    </Label>
                    <Input
                      type="text"
                      value={accountId}
                      onChange={(e) => {
                        setAccountId(e.target.value);
                        setSuccessMessage(null);
                      }}
                      placeholder="UUID"
                      required
                    />
                    {fieldErrors.account_id?.length ? (
                      <p className="mt-1 text-sm text-error-500">
                        {fieldErrors.account_id.join(' ')}
                      </p>
                    ) : null}
                  </div>
                  <div>
                    <Label>
                      Location ID <span className="text-error-500">*</span>
                    </Label>
                    <Input
                      type="text"
                      value={locationId}
                      onChange={(e) => {
                        setLocationId(e.target.value);
                        setSuccessMessage(null);
                      }}
                      placeholder="UUID"
                      required
                    />
                    {fieldErrors.location_id?.length ? (
                      <p className="mt-1 text-sm text-error-500">
                        {fieldErrors.location_id.join(' ')}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div>
                  <Label>
                    Connection ID <span className="text-error-500">*</span>
                  </Label>
                  <p className="mb-2 mt-1 text-xs leading-relaxed text-gray-500 dark:text-gray-400">
                    Pull the connection ID from the AskArthur marketplace URL in
                    the POSHub location dashboard.
                  </p>
                  <Input
                    type="text"
                    value={connectionId}
                    onChange={(e) => {
                      setConnectionId(e.target.value);
                      setSuccessMessage(null);
                    }}
                    placeholder="UUID"
                    required
                  />
                  {fieldErrors.connection_id?.length ? (
                    <p className="mt-1 text-sm text-error-500">
                      {fieldErrors.connection_id.join(' ')}
                    </p>
                  ) : null}
                </div>

                {error ? (
                  <div className="space-y-2">
                    <div className="rounded-lg border border-error-200 bg-error-50 px-4 py-3 text-sm text-error-700 dark:border-error-800 dark:bg-error-900/20 dark:text-error-300">
                      {error}
                    </div>
                    {errorCommandOutput ? (
                      <pre className="max-h-48 overflow-auto whitespace-pre-wrap rounded-lg border border-error-200/80 bg-error-50/50 p-3 text-xs text-error-800 dark:border-error-800 dark:bg-error-950/40 dark:text-error-200">
                        {errorCommandOutput}
                      </pre>
                    ) : null}
                  </div>
                ) : null}

                {successMessage ? (
                  <div className="rounded-lg border border-success-200 bg-success-50 px-4 py-3 text-sm text-success-700 dark:border-success-800 dark:bg-success-900/20 dark:text-success-300">
                    {successMessage}
                  </div>
                ) : null}

                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:opacity-50 dark:focus:ring-offset-gray-900"
                >
                  {submitting ? 'Submitting…' : 'Run manual setup'}
                </button>
              </form>
            </>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-300 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
              Select an integration above to open its manual setup form.
            </div>
          )}
        </div>
      </ComponentCard>

      {successPayload != null && typeof successPayload === 'object' ? (
        <ComponentCard title="Setup result">
          <div className="space-y-3 text-sm text-gray-800 dark:text-gray-200">
            {(successPayload as { created_new_connection?: boolean })
              .created_new_connection !== undefined && (
              <p>
                <span className="font-medium">Created new connection:</span>{' '}
                {(successPayload as { created_new_connection: boolean })
                  .created_new_connection
                  ? 'yes'
                  : 'no (updated / reused existing)'}
              </p>
            )}
            {(successPayload as { connection?: Record<string, unknown> })
              .connection && (
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/50">
                <p className="font-medium text-gray-900 dark:text-white mb-2">
                  Connection
                </p>
                <ul className="space-y-1 font-mono text-xs break-all">
                  {Object.entries(
                    (successPayload as { connection: Record<string, unknown> })
                      .connection,
                  ).map(([k, v]) => (
                    <li key={k}>
                      <span className="text-gray-500 dark:text-gray-400">
                        {k}:{' '}
                      </span>
                      {v === null || v === undefined
                        ? '—'
                        : typeof v === 'object'
                          ? JSON.stringify(v)
                          : String(v)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {(successPayload as { command_output?: string }).command_output ? (
              <div>
                <p className="font-medium text-gray-900 dark:text-white mb-1">
                  Command output
                </p>
                <pre className="max-h-64 overflow-auto rounded-lg border border-gray-200 bg-gray-100 p-3 text-xs text-gray-800 dark:border-gray-600 dark:bg-gray-950 dark:text-gray-300 whitespace-pre-wrap">
                  {
                    (successPayload as { command_output: string })
                      .command_output
                  }
                </pre>
              </div>
            ) : null}

            <details className="text-xs">
              <summary className="cursor-pointer text-gray-500 dark:text-gray-400">
                Raw JSON
              </summary>
              <pre className="mt-2 max-h-48 overflow-auto rounded bg-gray-100 p-2 dark:bg-gray-900">
                {JSON.stringify(successPayload, null, 2)}
              </pre>
            </details>
          </div>
        </ComponentCard>
      ) : null}
    </div>
  );
}
